import React, { useState, FormEvent, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext'; 
import CommonUtil from '../util/CommonUtil';
import { messaging } from "../config/firebase-config";
import {  getToken } from "firebase/messaging";
import AdComponent from './ads/AdComponent';
import { DashboardResponse, LeagueDto, LeagueStatus } from '@playball/common/dto/League';
import { DataResponse } from '@playball/common/dto/Common';
import { NewPodium } from './UserRankingPage';

interface CreateLeagueFormData {
    name: string;
    league_type: 'weekly' | 'season';
    draft_at: string;
    start_date: string;
}

const isSupportNotification = () =>
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window;


const LeagueDashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const [leagueCode, setLeagueCode] = useState('');
    const [formData, setFormData] = useState<CreateLeagueFormData>({
        name: '',
        league_type: 'weekly',
        draft_at: '',
        start_date: '',
    });


    // 대시보드 데이터, 로딩 상태, 에러 상태를 관리하기 위한 React State
    const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const { user, fetchApi } = useAuth();
    const [isPastLeaguesCollapsed, setIsPastLeaguesCollapsed] = useState(true);


    // --- 알림 권한 요청 로직 ---
    const requestNotificationPermission = useCallback(async () => {
        setShowNotificationModal(false);

        if (window.ReactNativeWebView) {
            const message = JSON.stringify({ type: 'requestNotificationPermission' });
            window.ReactNativeWebView.postMessage(message);
            window.notificationPermission = 'granted'
            return;
        }
    
        if (!isSupportNotification()) {
            alert("이 브라우저에서는 알림을 지원하지 않습니다. 다른 브라우저를 이용해주세요.");
            return;
        }
    
        if (sessionStorage.getItem('notificationPrompted')) {
            return;
        }
    
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted' && messaging) {
                const vapidKey = "BJop1qJkWq1Aq5ZCvjE4wbv6VvtDyq6NP31Usdss0tOel4Yw0lysa9oTtKJnWtWlb5A1Gnrdl20OkNlNREt3w2c";
                const currentToken = await getToken(messaging, { vapidKey });
                if (currentToken) {
                    await fetchApi('/api/my/notification', {
                        method: 'POST',
                        body: JSON.stringify({ token: currentToken }),
                    });
                    alert("알림이 설정되었습니다!");
                } else {
                  alert("알림이 허용되었습니다.");
                }
            } else {
                alert("알림이 허용되지 않았습니다.");
            }
        } catch (error) {
            console.error('알림 권한 요청 중 에러 발생:', error);
        }
    }, [fetchApi]);

    // 로그인한 사용자에게만 알림 권한 요청 모달을 띄우는 로직
    useEffect(() => {
        const dismissed = window.sessionStorage.getItem('externalBrowserPopupDismissed');
        if (dismissed) return;

        const isWebView = window.isReactNativeWebView === true;
        const notificationPermission = window.notificationPermission;
        const isAndroid = window.isAndroid;
        
        if (user && isWebView && isAndroid){
            //console.log("android webview, send token immediately")
            requestNotificationPermission();
        }
        
        if (user && ((isWebView && notificationPermission !== 'granted' )|| (isSupportNotification() && Notification.permission === 'default'))) {
            const timer = setTimeout(() => setShowNotificationModal(true), 5000); // 5초 후
            return () => clearTimeout(timer);
        }
    }, [requestNotificationPermission, user]);
    
    // 백엔드에서 대시보드 데이터를 가져오는 함수
    // useCallback으로 감싸 불필요한 함수 재생성을 방지
    const fetchDashboardData = useCallback(async () => {
        setIsLoading(true);
        try {
            // 백엔드의 API 엔드포인트 호출.
            // 이 엔드포인트는 현재 로그인한 사용자의 리그 목록과 참여 가능한 리그 정보를 반환해야 합니다.
            // `fetchApi`는 `AuthContext`에서 제공하며, 인증 쿠키를 자동으로 포함합니다.
            const data = await fetchApi('/api/league') as DataResponse<DashboardResponse>;
            setDashboardData(data.data);
            setError(null);
        } catch (err: any) {
            console.error("대시보드 데이터 로딩 실패:", err);
            setError(err.message || "데이터를 불러오는 데 실패했습니다. 잠시 후 다시 시도해 주세요.");
        } finally {
            setIsLoading(false);
        }
    }, [fetchApi]);

    // 컴포넌트가 처음 마운트될 때 데이터 로딩 함수 호출
    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // 리그 참가 신청 핸들러
    const handleApplyLeague = async (scheduleId: number) => {
        try {
            await fetchApi('/api/league/apply', {
                method: 'POST',
                body: JSON.stringify({ schedule_id: scheduleId }),
            });
            CommonUtil.logEvent("join_league", { join_type: "public" });
            alert('리그 참가 신청이 완료되었습니다.');
            // 리그 목록에 변경이 생겼으므로, 대시보드 데이터를 다시 불러와 화면을 업데이트합니다.
            fetchDashboardData();
        } catch (err: any) {
            alert(err.message || '리그 참가 신청 중 오류가 발생했습니다.');
        }
    };

    // 로딩 중일 때 UI
    if (isLoading) {
        return <div className="container mt-5 text-center"><h2> 로딩 중...</h2></div>;
    }

    // 에러 발생 시 UI
    if (error) {
        return <div className="container mt-5 alert alert-danger">{error}</div>;
    }

    if (!dashboardData){
        return <div> 대쉬보드 데이터 로딩 실패</div>
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            await fetchApi('/api/league/apply', {
                method: 'POST',
                body: JSON.stringify({ league_code: leagueCode }),
            });
            CommonUtil.logEvent("join_league", { join_type: "private" });
            alert('리그 참가 신청이 완료되었습니다.');
            // 성공 시 페이지를 새로고침하거나 대시보드로 이동할 수 있습니다.
            window.location.reload(); 
        } catch (err: any) {
            setError(err.message || '참가 신청 중 오류가 발생했습니다. 코드를 확인해주세요.');
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        setError(null);


        
        try {
            const localDraftAt = new Date(formData.draft_at);
            const draftAt = new Date(localDraftAt);
            const SERVER_TIMEZONE_OFFSET = draftAt.getTimezoneOffset() * 60 * 1000;
            const KST_TIMEZONE_OFFSET = -9 * 60 * 60 * 1000;
            draftAt.setTime(draftAt.getTime() + KST_TIMEZONE_OFFSET - SERVER_TIMEZONE_OFFSET);

            if (draftAt.getTime() <= CommonUtil.getUtcDate().getTime() + 1000 * 60 * 60) {
                alert('드래프트는 최소 1시간 이후부터 가능합니다.')
                return;
            }

            const localStartAt = new Date(formData.start_date);
            localStartAt.setHours(0, 0, 0, 0);            
            if (localStartAt <= localDraftAt) {
                alert('리그 시작 시각은 드래프트 다음 날부터 가능합니다.')
                return;
            }

            await fetchApi('/api/league/create', {
                method: 'POST',
                body: JSON.stringify(formData),
            });
            CommonUtil.logEvent("create_league", { league_type: formData.league_type });
            alert('리그가 성공적으로 생성되었습니다.');
             // 성공 시 페이지를 새로고침하여 리그 목록을 업데이트합니다.
            window.location.reload();
        } catch (err: any) {
            setError(err.message || '리그 생성 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    };

    const ongoingLeagues: LeagueDto[] = [];
    const pastLeagues: LeagueDto[] = [];
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    dashboardData.leagueList.forEach((league) => {
        const leagueStartDate = new Date(league.start_date);
        if (leagueStartDate < oneMonthAgo) {
            pastLeagues.push(league);
        } else {
            ongoingLeagues.push(league);
        }
    });

    const getButtonInfo = (status: LeagueStatus) => {
        switch (status) {
            case 'finished':
                return { text: '결과', className: 'btn-outline-secondary' };
            case 'canceled':
                return { text: '', className: '', disabled: true };
            default:
                return { text: '입장', className: 'btn-primary' };
        }
    };

    // 데이터가 성공적으로 로드되었을 때 UI
    return (
        <>
            <section className="guide-hero-section py-5 fade-in">
                <h1 className="text-center display-5 fw-bolder">
                    <span className="gradient-text">리그 대시보드</span>
                </h1>
            </section>
            <div className="container pb-3">
                <div className="row m-1 g-4 g-lg-5 ">
                    {/* 참가 중인 리그 */}
                    <div className="col-lg-7">
                        <div className="card shadow-lg">
                            <div className="card-body p-4 p-lg-5">
                                <h3 className="card-title h4 fw-bold mb-4">참가 중인 리그</h3>
                                <div className="list-group list-group-flush">
                                    {ongoingLeagues.length > 0 ? (
                                        ongoingLeagues.map(league => {
                                            const buttonInfo = getButtonInfo(league.status);
                                            return (
                                                <Link to={buttonInfo.disabled ? '#' : `/league/session/${league.code}`} key={league.code} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center px-0 py-3">
                                                    <div>
                                                        <p className={`mb-1 fw-semibold text-${league.status === 'canceled' ? 'muted' : 'dark'}`}>{league.name}</p>
                                                        <small className={`fw-medium ${league.status === 'canceled' && 'muted'}`}>
                                                            드래프트: {CommonUtil.getLocalDateTimeString(league.draft_at)}
                                                            {(league.status === 'ready' || league.status === 'draft') && ' - 진행 중'}
                                                        </small>
                                                        <br />
                                                        <small className={`fw-medium ${league.status === 'canceled' && 'muted'}`}>
                                                            시작날짜: {league.start_date}
                                                            {(league.status === 'canceled') && ' - 취소됨'}
                                                        </small>
                                                    </div>
                                                    { buttonInfo.text && <span className={`btn ${buttonInfo.className} rounded-pill`}>{buttonInfo.text}</span> }
                                                </Link>
                                            );
                                        })
                                    ) : (
                                        <p className="text-muted">참가 중인 리그가 없습니다.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <AdComponent adSlot='article' />

                        <div className="container mt-5">
                            <div className="row">
                                <div className="col-lg-6">
                                    <div className="card shadow-lg mb-4 clickable-card" onClick={() => navigate('/user-ranking')} style={{ cursor: 'pointer' }}>
                                        <div className="card-header bg-success text-white">
                                            <h3 className="h5 mb-0">일일 명예의 전당</h3>
                                        </div>
                                        <div className="card-body">
                                            <NewPodium topRanks={dashboardData.dailyRankList} user={user} simpleView={true} />
                                        </div>
                                        <div className="card-footer text-end">
                                            <span className="btn btn-sm btn-outline-secondary">자세히 보기 <i className="bi bi-arrow-right"></i></span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-6">
                                    <div className="card shadow-lg mb-4 clickable-card" onClick={() => navigate('/user-ranking')} style={{ cursor: 'pointer' }}>
                                        <div className="card-header bg-success text-white">
                                            <h3 className="h5 mb-0">주간 명예의 전당</h3>
                                        </div>
                                        <div className="card-body">
                                            <NewPodium topRanks={dashboardData.weeklyRankList} user={user} simpleView={true} />
                                        </div>
                                        <div className="card-footer text-end">
                                            <span className="btn btn-sm btn-outline-secondary">자세히 보기 <i className="bi bi-arrow-right"></i></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card mt-3 shadow-lg">
                            <div className="card-body p-4 p-lg-5">
                                <h3 className="card-title h4 fw-bold mb-4" onClick={() => setIsPastLeaguesCollapsed(!isPastLeaguesCollapsed)} style={{ cursor: 'pointer' }}>
                                    지난 리그 {isPastLeaguesCollapsed ? '▼' : '▲'}
                                </h3>
                                {!isPastLeaguesCollapsed && (
                                    <div className="list-group list-group-flush">
                                        {pastLeagues.length > 0 ? (
                                            pastLeagues.map(league => {
                                                const buttonInfo = getButtonInfo(league.status);
                                                return (
                                                    <Link to={buttonInfo.disabled ? '#' : `/league/session/${league.code}`} key={league.code} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center px-0 py-3">
                                                        <div>
                                                            <p className={`mb-1 fw-semibold text-muted`}>{league.name}</p>
                                                            <small className={`fw-medium text-muted`}>
                                                                시작날짜: {league.start_date}
                                                                {(league.status === 'canceled') && ' - 취소됨'}
                                                            </small>
                                                        </div>
                                                        { buttonInfo.text && <span className={`btn ${buttonInfo.className} rounded-pill`}>{buttonInfo.text}</span> }
                                                    </Link>
                                                );
                                            })
                                        ) : (
                                            <p className="text-muted">지난 리그가 없습니다.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>


                    {/* 리그 참여 및 생성 */}
                    <div className="col-lg-5">
                        <div className="d-flex flex-column gap-4">
                            <div className="card shadow-sm">
                                <div className="card-body p-4">
                                    <h4 className="card-title h6 fw-bold">퍼블릭 리그 참가</h4>
                                    {dashboardData?.nextWeeklySchedule ? (
                                        <>
                                            <p className="text-muted small mb-2">
                                                드래프트: {CommonUtil.getLocalDateTimeString(dashboardData.nextWeeklySchedule.draft_at)}
                                            </p>
                                            <button
                                                onClick={() => handleApplyLeague(dashboardData.nextWeeklySchedule!.id)}
                                                className="btn bg-gradient-custom w-100 text-white"
                                                disabled={dashboardData.nextWeeklySchedule.is_applicated}
                                            >
                                                {dashboardData.nextWeeklySchedule.is_applicated ? '신청 완료' : '참가 신청'}
                                            </button>
                                        </>
                                    ) : (
                                        <p className="text-muted small">신청 가능한 리그가 없습니다.</p>
                                    )}
                                </div>
                            </div>
                            
                            <div className="card shadow-sm">
                                <div className="card-body p-4">
                                    <h4 className="card-title h6 fw-bold">초대 코드로 참가</h4>
                                    <small className='form-text text-muted'>공유 받은 리그 초대 코드를 입력하세요</small>
                                    <form onSubmit={handleSubmit} className="mt-2">
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={leagueCode}
                                                onChange={(e) => setLeagueCode(e.target.value)}
                                                placeholder="리그 코드 입력"
                                                required
                                            />
                                            <button type="submit" className="btn btn-secondary text-white">참가</button>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            <div className="card shadow-sm">
                                <div className="card-body p-4">
                                    <h4 className="card-title h6 fw-bold">새로운 커스텀 리그 만들기</h4>
                                    <form id="createLeagueForm" onSubmit={handleCreate}>
                                        <div className="mb-3">
                                            <label htmlFor="name" className="form-label">리그 이름 <span className="text-danger">*</span></label>
                                            <input type="text" className="form-control" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="리그 이름을 입력하세요" required disabled={isLoading} />
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="league_type" className="form-label">리그 타입 <span className="text-danger">*</span></label>
                                            <select className="form-select" id="league_type" name="league_type" value={formData.league_type} onChange={handleChange} required disabled={isLoading}>
                                                <option value="weekly">주간 리그</option>
                                                
                                            </select>
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="draft_at" className="form-label">드래프트 시작 시각 <span className="text-danger">*</span></label>
                                            <input type="datetime-local" className="form-control" id="draft_at" name="draft_at" value={formData.draft_at} onChange={handleChange} required disabled={isLoading} />
                                        </div>
                                        <div className="mb-3">
                                            <label htmlFor="start_date" className="form-label">리그 시작 날짜 <span className="text-danger">*</span></label>
                                            <input type="date" className="form-control" id="start_date" name="start_date" value={formData.start_date} onChange={handleChange} required disabled={isLoading} />
                                        </div>
                                        {error && <div className="alert alert-danger">{error}</div>}
                                        <div className="d-grid gap-2">
                                            <button type="submit" className="btn btn-outline-secondary" disabled={isLoading}>
                                                {isLoading ? '생성 중...' : '리그 생성'}
                                            </button>
                                        </div>
                                        <small className="form-text text-muted">커스텀 리그는 2명 이상 모이지 않으면 자동으로 취소됩니다.</small>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            

            {/* 리그 생성 모달 (Bootstrap Modal 사용) */}
            <div className="modal fade" id="createLeagueModal" tabIndex={-1} aria-labelledby="createLeagueModalLabel" aria-hidden="true">
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title" id="createLeagueModalLabel">새 커스텀 리그 생성</h5>
                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                  </div>
                  <div className="modal-body">
                    <form id="createLeagueForm" onSubmit={handleCreate}>
                        <div className="mb-3">
                            <label htmlFor="name" className="form-label">리그 이름</label>
                            <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} required />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="draft_at" className="form-label">드래프트 시작 시각</label>
                            <input type="datetime-local" className="form-control" name="draft_at" value={formData.draft_at} onChange={handleChange} required />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="start_date" className="form-label">리그 시작 날짜</label>
                            <input type="date" className="form-control" name="start_date" value={formData.start_date} onChange={handleChange} required />
                        </div>
                        <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
                            {isLoading ? '생성 중...' : '생성하기'}
                        </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
            </div>

            {/* 알림 권한 요청 모달 */}
            {showNotificationModal && (
                <div className="bottom-alert">
                    <div className="alert-content">
                        <div className="alert-title">📢 경기 알림을 받고 싶으신가요?</div>
                        <div className="alert-description">드래프트 시작, 라인업 마감 등 중요한 알림을 놓치지 않도록 알려드릴게요.</div>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button className="btn btn-secondary" onClick={() => {
                                setShowNotificationModal(false);
                                window.sessionStorage.setItem("externalBrowserPopupDismissed", "true");
                            }}>
                                나중에 할게요
                            </button>
                            <button className="btn btn-primary" onClick={requestNotificationPermission}>알림 허용</button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .bottom-alert { position: fixed; bottom: 0; left: 0; width: 100%; background-color: rgba(0, 0, 0, 0.9); z-index: 9999; color: white; text-align: center; padding: 20px; box-sizing: border-box; }
                .alert-content { max-width: 400px; margin: 0 auto; }
                .alert-close { position: absolute; top: 10px; right: 10px; }
                .alert-title { font-size: 20px; font-weight: bold; margin-bottom: 15px; }
                .alert-description { font-size: 16px; margin-bottom: 30px; line-height: 1.5; }
                .alert-buttons { display: flex; flex-direction: column; gap: 10px; width: 100%; }
            `}</style>
        </>
        
    );
};

export default LeagueDashboardPage;